import React, { useState, useEffect, useRef } from "react";
import { Box, Text, Flex, Button, Link } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { texts } from "../../lang";
import { COLORS } from "../../globalvariables";
import { LANGUAGE_CHANGE_EVENT } from "../../context/LanguageContext";
import { useNavigate } from "react-router-dom";

const MotionBox = motion(Box);
const MotionImage = motion.img;
// Props: title, models (array of {id, title, description, image})
const ModelsSection = ({ title }) => {
  const [hovered, setHovered] = useState(null);
  const [active, setActive] = useState(null);
  const { language } = useLanguage();
  const navigate = useNavigate();
  // Get data from language file
  const { modelsSection } = texts[language];

  // Get only the first two models from the language file
  const modelItems = modelsSection.models;

  useEffect(() => {
    const handleClick = (e) => {
      const allowedSelectors = [
        ".card-active img", // image
        ".card-active h3", // title
        ".card-active .model-description", // description
        ".card-active .more-info-button", // button
        ".inactive-image", // other model image
        ".model-nav-button", // navigation buttons
      ];
      const isAllowed = allowedSelectors.some((selector) =>
        e.target.closest(selector)
      );
      if (!isAllowed) {
        setActive(null);
        setHovered(null);
      }
    };
    window.addEventListener("mousedown", handleClick);
    return () => window.removeEventListener("mousedown", handleClick);
  }, []);

  // Add effect to prevent body scroll when a model is active

  
  const scrollYRef = useRef(0);
const wasActiveRef = useRef(false);

useEffect(() => {
  if (active !== null && !wasActiveRef.current) {
    // Première ouverture
    scrollYRef.current = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollYRef.current}px`;
    wasActiveRef.current = true;
  }

  if (active === null && wasActiveRef.current) {
    // Fermeture
    const scrollY = parseInt(document.body.style.top || '0') * -1;
    document.body.style.overflow = 'unset';
    document.body.style.position = 'static';
    document.body.style.width = 'auto';
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
    wasActiveRef.current = false;
  }

  return () => {
    if (active === null && wasActiveRef.current) {
      // Cas où le composant unmount avec modale ouverte
      const scrollY = parseInt(document.body.style.top || '0') * -1;
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
      wasActiveRef.current = false;
    }
  };
}, [active]);

  // Nouvelle fonction d'effet pour écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      // Fermer la modale active lorsque la langue change
      setActive(null);
      setHovered(null);
    };

    // Ajouter l'écouteur d'événement
    document.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);

    // Nettoyage
    return () => {
      document.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, []);

  // Function to navigate to next or previous model
  const navigateModel = (direction) => {
    if (active === null) return;
    const currentIndex = modelItems.findIndex(
      (model) => model.id === modelItems[active].id
    );
    let newIndex;
    if (direction === "next") {
      newIndex = (currentIndex + 1) % modelItems.length;
    } else {
      newIndex = (currentIndex - 1 + modelItems.length) % modelItems.length;
    }
    setActive(newIndex);
  };

  // Function to handle navigation to the Machine page
  const handleDiscoverMachines = () => {
    navigate("/machine");
  };

  if (modelItems.length === 0) {
    return (
      <Box color={COLORS.textPrimary} textAlign="center" py={10}>
        Loading models...
      </Box>
    );
  }
  

  return (
    <Box
      w="100%"
      py={{ base: 8, md: 16 }}
      px={{ base: 2, md: 4 }}
      id="models"
      opacity={active === null ? 0.8 : 1}
    >
      <Text
        fontWeight="bold"
        textAlign="center"
        mb={{ base: 8, md: 20 }}
        fontSize={{ base: "1.8rem", md: "2.5rem" }}
        bgGradient={`linear(to-r, ${COLORS.textPrimary}, ${COLORS.primary} 30%, ${COLORS.textPrimary})`}
        bgClip="text"
        color="transparent"
        backgroundSize="200% 100%" // ↪️ force le dégradé à être "compressé"
        backgroundPosition="center" // ↪️ centre le dégradé visuellement
      >
        {modelsSection.title}
        <Box
          as="div"
          height="3px"
          width="60px"
          bg={COLORS.primary}
          borderRadius="3px"
          margin="8px auto 0"
        />
      </Text>
      <Flex
        justify="center"
        align="center"
        wrap={{ base: "wrap", md: "nowrap" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 6, md: 0 }}
        position="relative"
        w="100%"
        maxW="100vw"
      >
        {modelItems.map((model, index) => {
          const isHovered = hovered === index;
          const isActive = active === index;
          const isInactive = active !== null && !isActive;

          // For two models, position them left and right when active
          const positionOffset = index === 0 ? "-130%" : "130%";

          return (
            <Box
              key={model.id}
              onMouseEnter={() => !isActive && setHovered(index)}
              onMouseLeave={() => !isActive && setHovered(null)}
            >
              <MotionBox
              
                className={isActive ? "card-active" : ""}
                flex="1"
                maxW={{ base: "280px", md: "300px" }}
                minW="0"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive(index);
                }}
                position={isActive ? "fixed" : "relative"}
                top={isActive ? "55%" : "auto"}
                left={isActive ? "50%" : "auto"}
                alignSelf={isInactive ? "center" : "auto"}
                animate={{
                  scale: isActive ? (window.innerWidth <= 768 ? 1.0 : 1.05) : isInactive ? 0.8 : 1,
                  x: isActive
                    ? "-50%"
                    : isInactive
                    ? positionOffset
                    : "0%",
                  y: isActive ? "-50%" : isInactive ? "0%" : "0%",
                  opacity: isInactive ? 0.6 : 1,
                  zIndex: isActive ? 30 : isInactive ? 20 : 1,
                  height:
                    isHovered && !isActive ? "auto" : isActive ? (window.innerWidth <= 768 ? "75vh" : "75vh") : 350,
                  maxHeight: isActive ? (window.innerWidth <= 768 ? "75%" : "75%") : "460px",
                  width: isActive ? (window.innerWidth <= 768 ? "85%" : "40%") : "auto",
                  maxWidth: isActive ? "85vw" : (window.innerWidth <= 768 ? "280px" : "300px"),
                }}
                transition={{
                  duration: 0.5,
                  ease: [0.19, 1.0, 0.22, 1.0], // Improved easing for smoother animation
                }}
                pointerEvents="auto"
                m={{ base: "0 1rem", md: "0 2rem" }} // Increased margin for better spacing with only two models
                bg={COLORS.cardBackground}
                borderRadius="12px"
                overflow={isActive ? "auto" : "hidden"}
                boxShadow={
                  isActive || isHovered
                    ? `0 0 25px ${COLORS.primary}`
                    : "0 4px 6px rgba(0, 0, 0, 0.1)"
                }
              >
                <MotionBox
                  className={isInactive ? "inactive-image" : ""}
                  style={isInactive ? { zIndex: 30, position: "relative" } : {}}
                  w="100%"
                  h={isActive ? (window.innerWidth <= 768 ? "35vh" : "35vh") : (window.innerWidth <= 768 ? "200px" : "280px")}
                  overflow="hidden"
                  position="relative"
                >
                  <MotionBox
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg={COLORS.overlayBackground}
                    opacity={isHovered || isActive ? 0 : 0.2}
                    transition="opacity 0.3s ease"
                    zIndex="1"
                  />
                  <MotionImage
                    src={model.image}
                    alt={model.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    animate={{
                      scale: isActive || isHovered ? 1.05 : 1,
                      filter: isActive
                        ? "blur(0px)"
                        : isInactive
                        ? "blur(2px)"
                        : "blur(0px)",
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                </MotionBox>
                {isActive && (
                  <Button
                    position="absolute"
                    top={2}
                    right={2}
                    size="sm"
                    bg="rgba(0, 0, 0, 0.7)"
                    color="white"
                    _hover={{ bg: "rgba(0, 0, 0, 0.9)" }}
                    borderRadius="full"
                    minW="auto"
                    h="32px"
                    w="32px"
                    p={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActive(null);
                    }}
                    zIndex={40}
                  >
                    ✕
                  </Button>
                )}
                <Box pt={4} pr={4} pl={4} pb={isActive || isHovered ? 4 : 0}>
                  <Text
                    as="h3"
                    fontSize={isActive ? (window.innerWidth <= 768 ? "xl" : "2xl") : (window.innerWidth <= 768 ? "md" : "lg")}
                    fontWeight="600"
                    color={COLORS.textPrimary}
                    textAlign="center"
                    mb={isActive || isHovered ? 4 : 0}
                  >
                    {model.title}
                  </Text>
                  <Box
                    className="model-description"
                    style={{
                      minHeight: isActive ? "auto" : isHovered ? (window.innerWidth <= 768 ? "80px" : "100px") : "0",
                      height: isActive ? "auto" : isHovered ? (window.innerWidth <= 768 ? "80px" : "100px") : "0",
                      overflow: "hidden",
                    }}
                  >
                    {/* TEXTE SCROLLABLE UNIQUEMENT */}
                    <Box
                      style={{
                        maxHeight: isActive ? (window.innerWidth <= 768 ? "100px" : "130px") : (window.innerWidth <= 768 ? "80px" : "100px"),
                        overflowY: isActive ? "auto" : "hidden",
                        paddingRight: isActive ? "6px" : "0",
                        scrollbarWidth: "thin",
                        scrollbarColor: `${COLORS.primary} transparent`,
                        backgroundColor: COLORS.primary,
                        borderRadius: "8px",
                        padding:
                          isHovered && !isActive
                            ? (window.innerWidth <= 768 ? "8px" : "10px")
                            : isActive
                            ? (window.innerWidth <= 768 ? "8px" : "10px")
                            : "0",
                        transition:
                          "padding 0.3s ease, background-color 0.3s ease",
                      }}
                    >
                      <Box
                        css={{
                          "&::-webkit-scrollbar": {
                            width: window.innerWidth <= 768 ? "4px" : "6px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: COLORS.creamText,
                            borderRadius: "3px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                          },
                        }}
                        style={{
                          height: "100%",
                          overflowY: "auto",
                        }}
                      >
                        <Text 
                          color={COLORS.creamText}
                          fontSize={window.innerWidth <= 768 ? "sm" : "md"}
                        >
                          {isActive
                            ? model.description
                            : `${model.description.slice(0, window.innerWidth <= 768 ? 40 : 60)}${
                                model.description.length > (window.innerWidth <= 768 ? 40 : 60) ? "..." : ""
                              }`}
                        </Text>
                      </Box>
                    </Box>

                    {/* BOUTON FIXE EN BAS */}
                    {isActive && (
                      <Box pt={6} pb={4} textAlign="center">
                        <Button
                          className="more-info-button"
                          as={Link}
                          href={`/machine#model-${model.id}`}
                          bg="transparent"
                          color={COLORS.primary}
                          _hover={{
                            bg: "transparent",
                            color: COLORS.primaryHover,
                          }}
                          border={`2px solid ${COLORS.primary}`}
                          size={window.innerWidth <= 768 ? "sm" : "md"}
                          fontWeight="bold"
                          borderRadius="full"
                          px={window.innerWidth <= 768 ? 4 : 6}
                          fontSize={window.innerWidth <= 768 ? "sm" : "md"}
                          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                        >
                          {modelsSection.moreInfoButton}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </MotionBox>
            </Box>
          );
        })}
      </Flex>
      {active !== null && (
        <>
          <Box
            position="fixed"
            top={0}
            left={0}
            w="100vw"
            h="100vh"
            bg={COLORS.overlayBackground}
            zIndex={25}
            onClick={() => setActive(null)}
          />
          
          {/* Bouton de fermeture en haut à droite pour mobile */}
          <Button
            position="fixed"
            top={{ base: "20px", md: "30px" }}
            right={{ base: "20px", md: "30px" }}
            zIndex={40}
            onClick={() => setActive(null)}
            bg="rgba(255, 255, 255, 0.9)"
            color="black"
            _hover={{ bg: "rgba(255, 255, 255, 1)" }}
            borderRadius="full"
            w={{ base: "40px", md: "50px" }}
            h={{ base: "40px", md: "50px" }}
            fontSize={{ base: "20px", md: "24px" }}
            fontWeight="bold"
            display="flex"
            alignItems="center"
            justifyContent="center"
            border="2px solid rgba(0, 0, 0, 0.1)"
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.2)"
          >
            ✕
          </Button>
          
          {/* Navigation buttons - only show if there are multiple models */}
          {modelItems.length > 1 && (
            <Flex
              position="fixed"
              top="50%"
              left="0"
              right="0"
              zIndex={35}
              justify="space-between"
              px={{ base: "10%", md: "15%", lg: "20%" }}
              transform="translateY(-50%)"
            >
              <Button
                className="model-nav-button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateModel("prev");
                }}
                bg="whiteAlpha.600"
                color={COLORS.textPrimary}
                _hover={{ bg: COLORS.primary, color: COLORS.background }}
                borderRadius="full"
                w="50px"
                h="50px"
                fontSize="20px"
              >
                ←
              </Button>
              <Button
                className="model-nav-button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateModel("next");
                }}
                bg="whiteAlpha.600"
                color={COLORS.textPrimary}
                _hover={{ bg: COLORS.primary, color: COLORS.background }}
                borderRadius="full"
                w="50px"
                h="50px"
                fontSize="20px"
              >
                →
              </Button>
            </Flex>
          )}
        </>
      )}
      {/* Add the Discover Machines button after the models section */}
      {!active && (
        <Flex justifyContent="center" mt={16}>
          <Button
            onClick={handleDiscoverMachines}
            bg={COLORS.creamText}
            color={COLORS.primary}
            size="lg"
            border={`2px solid ${COLORS.primary}`}
            _hover={{
              bg: COLORS.buttonLightHover,
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
            }}
            _active={{
              bg: COLORS.buttonLightActive,
              transform: "translateY(0)",
              boxShadow: "none"
            }}
            fontWeight="bold"
            borderRadius="md"
            px={10}
            py={7}
            fontSize="xl"
          >
            {modelsSection.discoverButton}
          </Button>
        </Flex>
      )}
    </Box>
  );
};
export default ModelsSection;
